import React from 'react';
import { FormattedMessage } from 'react-intl';
import { InferredProgressOptions, Project } from 'api/project';
import { ResourceObject } from 'api/status-updates';
import { StatusGroup } from 'api/tasks';
import { Configuration } from 'api/timesheets/configuration/configuration';
import {
  formatter,
  formatterWithMinutes,
} from 'components/atoms/input-duration';
import { Progress as OzoneProgress } from 'components/atoms/progress';
import { Tag } from 'components/atoms/tag';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { csatRender } from 'components/organisms/projects/project-list/columns/renderer';
import { NoValue } from 'components/organisms/tasks/field-value/no-value';
import { FieldType, FilterableField } from 'components/organisms/tasks/fields';
import { isNonBillableHoursFlagEnabled } from 'components/organisms/timesheets/common/utils';
import { ProjectFieldsName } from 'routes/projects/list-v2/fields';
import { getRandomColor } from 'utils/get-random-color';
import {
  ProjectFinancialsContractTypeOptions,
  ProjectFinancialsSubscriptionFrequencyOptions,
  ProjectTimeTrackingFieldsName,
} from 'utils/project-fields/use-project-native-fields-as-filterable-fields';
import { PopoverStatus, StatusText } from '../projects/progress-status/popover';
import { StatusIndicatorIcon } from '../status-indicator';
import { LastFiveUpdates } from './last-five-updates';
import { Info } from './project-hours';
import { ProjectPhases } from './project-phases';
import { ProjectProgress } from './project-progress';
import {
  RenderCurrency,
  SingleSelect,
  SingleUser,
  MultiUser,
  MultiSelect,
  RenderBoolean,
  RenderText,
  TimeRenderer,
  NumberofDays,
  RatingRenderer,
} from './renderers';
import { MultiSelectWrapper, Rte, TextWrapper } from './style';
import { FieldTypeFromBackend, getUserId, getUserIds } from './utils';

type SummaryFieldRenderer = {
  field: FilterableField;
  projectDetails: Project;
  isPrivate?: boolean;
  isPublished?: boolean;
  publishedField?: ResourceObject;
  timesheetConfiguration: Configuration | null | undefined;
};

export const getSummaryFieldRenderer = ({
  field,
  projectDetails,
  isPrivate,
  isPublished,
  publishedField,
  timesheetConfiguration,
}: SummaryFieldRenderer) => {
  const customFieldsData = projectDetails.fields;

  const currentCustomField = isPublished
    ? publishedField
    : customFieldsData.find(
        (customField) => customField.fieldLabel === field.fieldLabel,
      );
  const fieldData = isPublished
    ? currentCustomField?.fieldValue ||
      (field as unknown as ResourceObject)?.fieldValue
    : projectDetails[field.fieldName] ||
      projectDetails.metrics[field.fieldName] ||
      (currentCustomField as ResourceObject)?.fieldValue;

  if (field.fieldName === ProjectFieldsName.arr) {
    return (
      <RenderCurrency
        value={
          isPublished
            ? publishedField?.fieldValue?.arr ?? 0
            : projectDetails?.annualizedRecurringRevenue
        }
        currency={projectDetails?.projectFinancialsDetailModel?.currency}
      />
    );
  }
  if (field.fieldName === ProjectFieldsName.projectFee) {
    return (
      <RenderCurrency
        value={
          isPublished ? publishedField?.fieldValue : projectDetails.projectFee
        }
        currency={projectDetails?.projectFinancialsDetailModel?.currency}
      />
    );
  }
  if (field.fieldName === ProjectFieldsName.projectBudget) {
    return (
      <RenderCurrency
        value={
          isPublished
            ? publishedField?.fieldValue
            : projectDetails?.projectFinancialsDetailModel?.projectBudget
        }
        currency={projectDetails?.projectFinancialsDetailModel?.currency}
      />
    );
  }
  if (field.fieldName === ProjectFieldsName.actualRevenue) {
    return (
      <RenderCurrency
        value={
          isPublished
            ? publishedField?.fieldValue ?? 0
            : projectDetails?.metrics?.actualRevenue
        }
        currency={projectDetails?.projectFinancialsDetailModel?.currency}
      />
    );
  }
  if (field.fieldName === 'customer') {
    return (
      <SingleSelect
        color={getRandomColor(
          isPublished
            ? publishedField?.fieldValue
            : projectDetails[field.fieldName].companyName,
        )}
        label={
          isPublished
            ? publishedField?.fieldValue
            : projectDetails[field.fieldName].companyName
        }
      />
    );
  }
  if (field.fieldName === 'currentPhases') {
    let currentPhases = isPublished
      ? publishedField?.fieldValue
      : projectDetails[field.fieldName];

    if (!isPrivate) {
      currentPhases = isPublished
        ? currentPhases
        : currentPhases?.filter((phase) => phase.private === false);
    }

    if (!currentPhases || currentPhases.length === 0) {
      return <NoValue />;
    }

    return (
      <MultiSelectWrapper>
        {currentPhases
          ?.slice(0, 5)
          ?.filter((phase) =>
            !isPrivate && !isPublished ? phase.private === false : true,
          )
          .map((phase) => (
            <SingleSelect
              color=""
              label={isPublished ? phase : phase.projectPhaseName}
              key={isPublished ? phase : phase.startDate}
            />
          ))}
        {currentPhases.length > 5 ? (
          <Tag color={'blue' || getRandomColor('blue')}>{`+ ${
            currentPhases.length - 5
          }`}</Tag>
        ) : null}
      </MultiSelectWrapper>
    );
  }
  if (field.fieldName.toLowerCase() === ProjectFieldsName.status) {
    if (currentCustomField) {
      const { metaFieldValue } = currentCustomField as any;

      return (
        <>
          <SingleSelect
            color={metaFieldValue?.color || ''}
            label={metaFieldValue?.label}
          />
        </>
      );
    }

    return <NoValue />;
  }
  if (field.fieldName === ProjectFieldsName.plannedDuration) {
    const { plannedDuration } = projectDetails;

    return (
      <NumberofDays
        value={isPublished ? publishedField?.fieldValue : plannedDuration}
      />
    );
  }
  if (field.fieldName === 'projectAge') {
    const { projectAge } = projectDetails;

    const isNotEmpty = isPublished
      ? publishedField?.fieldValue === 0
      : projectAge === 0;

    if (isNotEmpty) {
      return <NumberofDays value={1} />;
    }

    return (
      <NumberofDays
        value={isPublished ? publishedField?.fieldValue : projectAge}
      />
    );
  }
  if (field.fieldName === ProjectTimeTrackingFieldsName.allocatedHours) {
    const { metrics } = projectDetails;
    const { totalAllocatedHours } = metrics;

    if (totalAllocatedHours || publishedField?.fieldValue) {
      return (
        <RenderText
          value={formatterWithMinutes(
            isPublished
              ? publishedField?.fieldValue ?? 0
              : totalAllocatedHours
              ? totalAllocatedHours * 60
              : 0,
          )}
        />
      );
    }

    return <RenderText value={formatterWithMinutes(0)} />;
  }
  if (field.fieldName === 'totalMinutes') {
    return (
      <RenderText
        value={formatter(
          isPublished
            ? publishedField?.fieldValue ?? 0
            : (projectDetails?.metrics?.trackedHours ?? 0) * 60,
        )}
      />
    );
  }
  if (field.fieldName === ProjectTimeTrackingFieldsName.budgetedMinutes) {
    const { budgetedMinutes } = projectDetails;

    const isEmpty = isPublished
      ? !publishedField?.fieldValue
      : !budgetedMinutes;

    if (isEmpty) {
      return <NoValue />;
    }

    return (
      <RenderText
        value={formatter(
          isPublished ? publishedField?.fieldValue ?? 0 : budgetedMinutes || 0,
        )}
      />
    );
  }
  if (field.fieldName === 'lastStatusUpdate') {
    const { pastProjectStatus } = projectDetails;

    if (isPublished ? publishedField?.fieldValue : pastProjectStatus) {
      if (
        (isPublished ? publishedField?.fieldValue : pastProjectStatus)
          .length !== 0
      ) {
        const date = isPublished
          ? publishedField?.fieldValue
          : pastProjectStatus[0]?.updatedAt;

        return <TimeRenderer time={date} />;
      }
    }

    return <NoValue />;
  }
  if (field.fieldName === ProjectFieldsName.csatRating) {
    return <>{csatRender(fieldData)}</>;
  }
  if (field.fieldName === ProjectFieldsName.lastMilestoneCSAT) {
    return <>{csatRender(fieldData)}</>;
  }
  if (field.fieldName === ProjectFieldsName.pastProjectStatus) {
    return (
      <LastFiveUpdates
        data={
          isPublished
            ? publishedField?.fieldValue
            : projectDetails.pastProjectStatus
        }
        projectId={projectDetails.projectId}
      />
    );
  }
  if (field.fieldName === ProjectFieldsName.projectMinutes) {
    const projectHours = {
      budgetMinutes: isPublished
        ? publishedField?.fieldValue?.budgetedMinutes ?? 0
        : projectDetails?.budgetedMinutes !== undefined
        ? projectDetails.budgetedMinutes
        : undefined,
      total_minutes: isPublished
        ? publishedField?.fieldValue?.trackedHours * 60 ?? 0
        : projectDetails?.metrics.trackedHours !== undefined
        ? projectDetails.metrics.trackedHours * 60
        : 0,
      project_name: projectDetails ? projectDetails.projectName : '',
      nonBillableHoursFlagEnabled:
        isNonBillableHoursFlagEnabled(
          projectDetails.financialContractType,
          timesheetConfiguration?.nonBillableHoursFlagSetting,
        ) &&
        (isPublished
          ? publishedField?.fieldValue?.billableHours !== undefined
          : projectDetails?.metrics?.billableHours !== undefined),
      billableMinutes: isPublished
        ? publishedField?.fieldValue?.billableHours * 60 ?? 0
        : projectDetails?.metrics?.billableHours !== undefined
        ? projectDetails.metrics?.billableHours * 60
        : 0,
    };

    return <Info projectHours={projectHours} />;
  }
  if (field.fieldName === ProjectFieldsName.projectPhases) {
    const isNotEmpty = isPublished
      ? publishedField?.fieldValue && publishedField?.fieldValue?.length
      : projectDetails?.projectPhases && projectDetails?.projectPhases?.length;

    if (isNotEmpty) {
      return (
        <ProjectPhases
          data={
            isPublished
              ? publishedField?.fieldValue
              : projectDetails?.projectPhases
          }
          isPrivate={isPrivate}
        />
      );
    }

    return <NoValue />;
  }
  if (field.fieldName === ProjectFieldsName.progressPercent) {
    const isNotEmpty = isPublished
      ? publishedField?.fieldValue
      : projectDetails.progressStatus;

    if (isNotEmpty && isPublished) {
      return (
        <>
          <OzoneProgress
            percent={
              publishedField?.fieldValue?.completedTasks ??
              0 + publishedField?.fieldValue?.inProgressTasks ??
              0
            }
            success={{
              percent: publishedField?.fieldValue?.completedTasks ?? 0,
            }}
            showInfo={false}
            strokeWidth={16}
            strokeColor="var(--green-30)"
          />
          <PopoverStatus>
            <StatusIndicatorIcon
              color="green"
              group={StatusGroup.Completed}
              progress={publishedField?.fieldValue?.completedTasks ?? 0}
            />
            <StatusText>
              <FormattedMessage
                id="projects.list.popover.current_phase.label"
                values={{
                  taskCount: publishedField?.fieldValue?.completedTasks ?? 0,
                  status: 'Completed',
                }}
              />
            </StatusText>
          </PopoverStatus>
          <PopoverStatus>
            <StatusIndicatorIcon
              color="green"
              group={StatusGroup.InProgress}
              progress={publishedField?.fieldValue?.inProgressTasks ?? 0}
            />
            <StatusText>
              <FormattedMessage
                id="projects.list.popover.current_phase.label"
                values={{
                  taskCount: publishedField?.fieldValue?.inProgressTasks ?? 0,
                  status: 'In progress',
                }}
              />
            </StatusText>
          </PopoverStatus>
          <PopoverStatus>
            <StatusIndicatorIcon
              color="blue"
              group={StatusGroup.Todo}
              progress={publishedField?.fieldValue?.totalTasks ?? 0}
            />
            <StatusText>
              <FormattedMessage
                id="projects.list.popover.current_phase.label"
                values={{
                  taskCount: publishedField?.fieldValue?.totalTasks ?? 0,
                  status: 'Total',
                }}
              />
            </StatusText>
          </PopoverStatus>
        </>
      );
    }
    if (isNotEmpty && !isPublished) {
      return <ProjectProgress data={projectDetails} />;
    }

    return <NoValue />;
  }
  if (field.fieldName === 'partnerCompany') {
    const partnerDetails = isPublished
      ? publishedField?.fieldValue ?? []
      : projectDetails.partners;

    if (!partnerDetails.length) {
      return <NoValue />;
    }

    return (
      <MultiSelectWrapper>
        {partnerDetails?.map((partner) => (
          <SingleSelect
            color=""
            label={partner.companyName}
            key={partner.companyId}
          />
        ))}
      </MultiSelectWrapper>
    );
  }
  if (field.fieldType === FieldTypes.Rating) {
    return (
      <RatingRenderer
        value={currentCustomField?.fieldValue}
        scale={(currentCustomField as any)?.metaData?.ratingScale}
      />
    );
  }
  if (field.fieldName === ProjectFieldsName.financialContractType) {
    const selectedOption = ProjectFinancialsContractTypeOptions.find(
      ({ value }) => value === fieldData,
    );

    return (
      <SingleSelect
        color={selectedOption?.color ?? ''}
        label={isPublished ? publishedField?.fieldValue : selectedOption?.label}
      />
    );
  }

  if (field.fieldName === ProjectFieldsName.subscriptionFrequency) {
    const data =
      projectDetails.projectFinancialsDetailModel?.projectFinancialsMetaData
        ?.subscriptionFrequency;

    const selectedOption = ProjectFinancialsSubscriptionFrequencyOptions.find(
      ({ value }) => value === data,
    );

    return (
      <SingleSelect
        color={selectedOption?.color ?? ''}
        label={selectedOption?.label}
      />
    );
  }
  if (field.fieldName === ProjectFieldsName.numberOfPeriods) {
    const data = isPublished
      ? publishedField?.fieldValue ?? ''
      : projectDetails.currentPeriodData?.numberOfPeriods;

    return <RenderText value={data} />;
  }

  if (
    !projectDetails[field.fieldName] &&
    !projectDetails.metrics[field.fieldName] &&
    !currentCustomField
  ) {
    return <NoValue />;
  }

  if (field.fieldName === ProjectFieldsName.projectOwner && isPublished) {
    return (
      <SingleUser
        userId={publishedField?.fieldValue?.userId ?? -1}
        user={publishedField?.fieldValue}
        isPublished={isPublished}
      />
    );
  }

  if (field.fieldName === ProjectFieldsName.teamMembers && isPublished) {
    return (
      <MultiUser
        users={publishedField?.fieldValue ?? []}
        isPublished={isPublished}
      />
    );
  }

  if (field.fieldName === ProjectFieldsName.projectName && isPublished) {
    if (publishedField?.fieldValue?.projectName) {
      return (
        <TextWrapper>
          {publishedField?.fieldValue?.projectName ?? ''}
        </TextWrapper>
      );
    }

    return <NoValue />;
  }

  const fieldTypeToCheck = isPublished ? FieldTypeFromBackend : FieldType;

  switch (
    isPublished
      ? publishedField?.customField
        ? publishedField?.fieldDataType
        : field.fieldType ?? publishedField?.fieldDataType
      : (field as FilterableField).type
  ) {
    case fieldTypeToCheck.Text:
    case fieldTypeToCheck.MultiLineText:
      return <Rte data={fieldData} readonly />;
    case fieldTypeToCheck.Number:
      return <RenderText value={fieldData} />;
    case fieldTypeToCheck.Date:
      return <TimeRenderer time={fieldData} />;
    case fieldTypeToCheck.SingleUser: {
      return (
        <SingleUser
          userId={getUserId(
            field as FilterableField,
            projectDetails,
            currentCustomField as any,
          )}
          user={publishedField?.fieldValue}
          isPublished={isPublished}
        />
      );
    }
    case fieldTypeToCheck.MultiUser:
      return (
        <MultiUser
          userIds={getUserIds(
            field as FilterableField,
            projectDetails,
            currentCustomField as any,
          )}
          users={publishedField?.fieldValue ?? []}
          isPublished={isPublished}
        />
      );
    case fieldTypeToCheck.SingleSelect: {
      const labelAndColorRenderer = () => {
        const valueExtractor = () => {
          return {
            label:
              InferredProgressOptions.find(
                (option) => option.value === currentCustomField?.fieldValue,
              )?.label ?? publishedField?.fieldValue,
            color:
              InferredProgressOptions.find(
                (option) => option.value === currentCustomField?.fieldValue,
              )?.color ?? 'gray',
          };
        };

        if (
          isPublished &&
          field.fieldName === ProjectFieldsName.inferredProgress
        ) {
          return {
            label: valueExtractor().label,
            color: valueExtractor().color,
          };
        }
        if (
          isPublished &&
          field.fieldName !== ProjectFieldsName.inferredProgress
        ) {
          return {
            label:
              currentCustomField?.metaFieldValue?.label ??
              publishedField?.fieldValue,
            color:
              currentCustomField?.metaFieldValue?.color ??
              publishedField?.fieldValue,
          };
        }

        return {
          label:
            projectDetails.fields[field.fieldName]?.label ??
            currentCustomField?.metaFieldValue?.label ??
            '',
          color:
            projectDetails.fields[field.fieldName]?.color ??
            currentCustomField?.metaFieldValue?.color ??
            '',
        };
      };

      return (
        <SingleSelect
          color={labelAndColorRenderer().color}
          label={labelAndColorRenderer().label}
        />
      );
    }
    case fieldTypeToCheck.MultiSelect:
      return (
        <MultiSelect
          options={
            isPublished
              ? (currentCustomField as ResourceObject)?.metaFieldValue ||
                publishedField?.fieldValue
              : projectDetails[field.fieldName] ||
                currentCustomField?.metaFieldValue ||
                undefined
          }
        />
      );
    case fieldTypeToCheck.Boolean:
      return <RenderBoolean value={fieldData} />;
    default:
      return <NoValue />;
  }
};
